import { useEffect, useState } from 'react';
import {
  Alert, Box, CircularProgress, MenuItem, Stack, TextField, Typography,
} from '@mui/material';

import { api, basemapUrlFor } from '../api/client';
import type { Category, City } from '../api/types';
import { PlaceBrowser } from '../components/PlaceBrowser';
import { pickDefaultCity, rememberCity } from '../util/defaultCity';

/*
 * The place browser, on a page of its own.
 *
 * The same browser the planner opens in a window, reached without a round in
 * hand: somebody asking "what has this team already covered, and where is
 * there still new ground?" is not mid-plan, and should not have to start a
 * round to ask it. So this adds the two things a window inside the planner
 * gets for free — a city to read, and a page to read it on — and nothing
 * else. Every figure, filter and marker is PlaceBrowser's, so the two
 * screens cannot disagree.
 *
 * Reading only: the three things a place can be to a round are the planner's
 * business, and there is no round here to make it part of.
 */

export function CoveragePage() {
  const [cities, setCities] = useState<City[] | null>(null);
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [city, setCity] = useState<City | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.cities().then(setCities).catch(() => setFailed(true));
    api.settings()
      .then(setSettings)
      .catch(() => { /* Defaults are a fine answer; see pickDefaultCity. */ })
      .finally(() => setSettingsLoaded(true));
  }, []);

  /*
    The city to open on, once there is something to choose from.

    Waits for the settings as well as the cities: picking the first and then
    jumping to the configured one a moment later would reload the catalogue
    under somebody already reading it.
  */
  useEffect(() => {
    if (city || !cities?.length || !settingsLoaded) return;
    setCity(pickDefaultCity(cities, settings));
  }, [city, cities, settings, settingsLoaded]);

  /* So the next screen opens on it, rather than on the configured default. */
  useEffect(() => { rememberCity(city); }, [city]);

  useEffect(() => {
    if (!city) return undefined;

    let cancelled = false;
    api.cityCategories(city.id)
      .then((found) => { if (!cancelled) setCategories(found); })
      .catch(() => { if (!cancelled) setCategories([]); });

    return () => { cancelled = true; };
  }, [city]);

  const basemapUrl = city
    ? basemapUrlFor(city, settings['map.shared_basemap'] === true)
    : null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ px: 2, py: 1.5, alignItems: 'center', flexWrap: 'wrap' }}
        useFlexGap
      >
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h2">Coverage</Typography>
          <Typography variant="caption" color="text.secondary">
            Every place this city's saved plans stop at — by whom, how often,
            and how recently.
          </Typography>
        </Box>

        {/*
          Which city is being read.

          A select rather than the planner's search box: this is a question
          about a whole city's record, and there are few enough of them to
          show.
        */}
        <TextField
          select
          size="small"
          label="City"
          value={city?.id ?? ''}
          onChange={(e) => {
            setCity(cities?.find((c) => c.id === e.target.value) ?? null);
          }}
          sx={{ minWidth: 220 }}
          disabled={!cities?.length}
        >
          {(cities ?? []).map((option) => (
            <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, borderTop: 1, borderColor: 'divider' }}>
        {failed ? (
          <Alert severity="warning" variant="outlined" sx={{ m: 2 }}>
            The cities could not be read.
          </Alert>
        ) : !city ? (
          <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {cities?.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No city has been imported yet.
              </Typography>
            ) : (
              <CircularProgress size={24} />
            )}
          </Stack>
        ) : (
          <PlaceBrowser
            city={city}
            categories={categories}
            basemapUrl={basemapUrl}
            plannedOnly
          />
        )}
      </Box>
    </Box>
  );
}
